import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import _ from 'lodash';
import { ToastContainer, toast as setToast, cssTransition } from 'react-toastify';
import { isBefore } from 'date-fns'
import { CSSTransition as Transition } from 'react-transition-group';

import { Button } from 'components'
import { ItemModal } from 'features'
import ToastBlock from './ToastBlock'
import { FaTimes } from 'react-icons/fa'

import * as actionCreators from 'store/actions'
import { momentDate, genNotiMessage } from 'utils';
import 'react-toastify/dist/ReactToastify.minimal.css';
import "./styles.css";
import * as api from 'api'
import SubmarkLogo from 'resources/Logo/submark-logo-4-192.png'

const CloseContainer = styled.div`
    color: var(--secondary-accent-color);
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 0.5rem;
    align-items: center;
    font-size: 0.8rem;
    font-weight: bolder;
    position: fixed;
    z-index: 1301;
    top: 3rem;
    right: 1em;
    cursor: pointer;
    background-color: var(--secondary-color);
    box-shadow: var(--box-shadow);
    padding: 0.5rem;
    margin-right: 4px;
    border-radius: 5px;
    :hover {
        color: white;
        background-color: var(--focus-color);
    }
`

const flip = cssTransition({
    enter: "flip-in-hor-bottom",
    exit: "flip-out-hor-bottom"
});

const CustomToastContainer = styled(ToastContainer).attrs({
    // custom props
})`
    // CSS props for Toastify__toast-container
    display: flex;
    flex-direction: column;
    margin-top: 4.2rem;
    /* position: absolute; */

    .Toastify__toast-container {
        display: flex;
        flex-direction: column;
    }
    .Toastify__toast-container--top-right {
        display: flex !important;
        flex-direction: column !important;
    }
    .Toastify__toast {
        min-height: 1rem;
        margin-bottom: 0.5rem;
        padding: 0.75rem;
        background-color: ${props => props.backgroundColor || 'var(--secondary-color)'};
        box-shadow: ${props => props.color 
                ? "0px 2px 1px -1px " + props.color + 
                ", 0px 1px 1px 0px " + props.color +
                ", 0px 1px 3px 0px " + props.color
                : "0px 2px 1px -1px var(--primary-accent2-color)," + 
                "0px 1px 1px 0px var(--primary-accent2-color), " +
                "0px 1px 3px 0px var(--primary-accent2-color)"};
    }
    .Toastify__toast--default {
        color: var(--text-color);
    }
    .Toastify__toast--info {
        color: #fff;
        background-color: var(--focus-color);
    }
    .Toastify__toast--error {}
    .Toastify__toast--warning {}
    .Toastify__toast--success {}
    .Toastify__toast-body {
        padding: 0;
    }
    .Toastify__progress-bar {}
    .Toaster__close-all-button {
        padding: 0.5rem;
        align-self: flex-end;
    }
`;

const Toaster = props => {
    const debug = false
    const dispatch = useDispatch()
    const [toasts, setToasts] = React.useState([])
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const alertData = useSelector(state => state.db.present.alerts)
    const toastData = useSelector(state => state.content.toasts)
    const workflows = useSelector(state => state.integration.workflows)
    const user = useSelector(state => state.auth.user)
    const [showClose, setClose] = React.useState(false)
    const [timeouts, setTimeouts] = React.useState([])

    // React.useEffect(() => {
    //     setToast.onChange((numberOfToastDisplayed, containerId) => {
    //         numberOfToastDisplayed === 0 && setClose(false)
    //         numberOfToastDisplayed >= 1 && setClose(true)
    //         // if(containerId === 'first'){
    //         //     numberOfToastDisplayed === 0 && setClose(false)
    //         //     numberOfToastDisplayed >= 1 && setClose(true)
    //         // }
    //     });
    // })

    React.useEffect(() => {
        user && setupToaster()
    }, [alertData, toastData])

    React.useEffect(() => {
        const unsubscribe = setToast.onChange((n) => {
            setClose(n > 0)
        })
        return () => unsubscribe();
    }, [])

    const getAlerts = () => {
        // Sort and generate alerts
        let alerts = Object.values(data['alerts']).sort((a,b) => (new Date(a.date) - new Date(b.date)))
        alerts = alerts.reverse()
        alerts = alerts.filter(alert => {
            if(isBefore(new Date(alert.date), new Date())){
                return false 
            } else {
                if(alert['secID']['relation'] === 'item'){
                    const itemIDs = Object.keys(data['items'])

                    // Filter deleted items
                    if(itemIDs.includes(alert['secID']['id'])){
                        const item = data['items'][alert['secID']['id']]
                        const includes = Object.values(item['alerts']).includes(alert['alertID'])
                        const incomplete = item['status'] === 'INCOMPLETE'

                        // Filter old alerts and completed items
                        if(includes & incomplete){
                            return true
                        } else {
                            return false
                        }
                    } else {
                        return false
                    }
                }
            }
        })
        return alerts
    }

    const clearTimeouts = () => {
        timeouts.forEach(timeout => window.clearTimeout(timeout))
        setTimeouts([])
    }

    const setupToaster = () => {
        clearTimeouts()
        const alerts = getAlerts()
        let updatedTimeouts = []

        // 1. Toasts for alerts
        alerts.forEach(alert => {
            const item = data['items'][alert['secID']['id']]
            if(!item) { return }

            const title = item.getPlainText()
            const subtitle = momentDate(item['start_time'] || item['end_time'] || item['start_date'] || item['end_date'], user['timezone'])
                            .format(!item['start_time'] & !item['end_time']? 'ddd, MMM D Y' : 'ddd, MMM D Y (h:mma)')

            // Get delay if got time set
            const now = new Date()
            let target = new Date(alert.date)
            target.setSeconds(0,0)
            const delay = alert.date && target.getTime() - now.getTime()
            // const delay = 3000

            let options = {
                toastId: alert['alertID'],
                delay: delay,
                containerId: 'first',
                // onOpen: (e) => setClose(true)
            }   
            const toast = {...options}        
            toast['title'] = title
            toast['subtitle'] = subtitle

            // Set the timeout for toasts
            if(delay > 0 && delay < 2147483647){
                if(user['app_notifications'] && !setToast.isActive(alert['alertID'])){
                    updatedTimeouts.push(
                        setTimeout(() => {
                            // Create toast
                            setToast(
                                <ToastBlock title={title} 
                                    subtitle={subtitle} 
                                    onSelect={() => item && dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item.itemID}_ItemModal`))}/>, 
                                _.omit(options, 'delay')
                            )

                            // Get Telegram workflows
                            const teleWorkflows = workflows &&  Object.values(workflows).filter(workflow => 
                                workflow.action['client'] === 'TELEGRAM' 
                                && workflow.active && workflow.trigger['trigger'] === 'StartScheduleItem'
                                && (workflow.trigger.pageID === 'ANY' || workflow.trigger.pageID === item['pageID'])
                                && (workflow.trigger.sectionID === 'ANY' || workflow.trigger.sectionID === item['sectionID'])
                            )
                            const teleChats = teleWorkflows && teleWorkflows.length > 0 && _.compact(teleWorkflows.map(workflow => workflow.action['clientId']))
                            if(teleChats.length > 0){
                                teleChats.map(chatId => {
                                    const message = genNotiMessage(item, this.props.data, this.props.user['timezone'])
                                    api.sendTele(chatId, { message: message })
                                })
                            }
                        }, delay)
                    )
                }

                if(user['desktop_notifications']){
                    updatedTimeouts.push(
                        setTimeout(() => {
                            const noti = new Notification(`Atomicly item starting soon: ${title}`, { requireInteraction: true, silent: false, body: subtitle, icon: SubmarkLogo, tag: alert['alertID'], data: { itemID: item['itemID'] } })        
                            noti.onclick = (event) => {
                                const deepLink = `com.atomicly:/?uid=${user['uid']}&itemId=${item['itemID']}`
                                window.location = deepLink
                                // item && this.props.setModal(<ItemModal item={item}/>, `${item.itemID}_ItemModal`)
                                // window.open(`${process.env.REACT_APP_BASE_URL}/${item['pageID']}/${item['itemID']}`)
                                // window.open(deepLink)
                            }
                        }, delay)
                    )
                }
            }

        })

        // 2. Toasts for ad-hoc
        toastData.length > 0 && toastData.forEach(toast => {
            setToast(
                <ToastBlock title={toast['title']} subtitle={toast['subtitle']}
                    onSelect={() => toast['onSelect'] && toast['onSelect']()}/>,
                {
                    toastId: toast['id'],
                    containerId: 'first',
                    type: 'warning'
                }
            )
            setClose(true)
        })
        setTimeouts(updatedTimeouts)
        debug && console.log('[SETUP TOASTER]',{timeouts, updatedTimeouts, alerts, alertData})
        // updatedTimeouts.length > 0 && setClose(true)
    }

    //////////////
    // HANDLERS //
    //////////////
    const onDismissAll = () => {
        setToast.dismiss()
        setClose(false)
    }

    return (
        <div>
            <Transition in={showClose} timeout={300} classNames={'alert'} unmountOnExit>
                <CloseContainer onClick={onDismissAll}>
                    <div>Dismiss all</div>
                    <FaTimes/>
                </CloseContainer>
            </Transition>
            
            <CustomToastContainer 
                closeButton={<Button icon={<FaTimes/>} styles={{margin: 0, padding: 0, boxShadowColor: 'transparent'}}/>}
                containerId='first' enableMultiContainer={true}
                transition={flip}
                position="top-right"
                autoClose={60000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover/>
        </div>
    )
}

export default Toaster